import { type Location } from '../../../shared/types';
import { getCountryByCountryCode, type IsoCountryCode2Alpha } from '../../helper/iso3166Utils';

export function sortAndGroupVenues(locations: Location[]): Map<
    string,
    {
        displayName: string;
        states: Map<string, Array<{ displayName: string; id: string }>>;
    }
> {
    return locations
        .map(l => ({
            ...l,
            countryName: getCountryByCountryCode(l.country as keyof typeof IsoCountryCode2Alpha),
            countryCode: l.country,
        }))
        .filter(
            (currentLocation, index, arr) =>
                arr.findIndex(
                    compareLocation => compareLocation.externalId === currentLocation.externalId,
                ) === index,
        )
        .sort(
            (a, b) =>
                a.country.localeCompare(b.country) ||
                a.state.localeCompare(b.state) ||
                a.city.localeCompare(b.city),
        )
        .reduce(
            (groupedVenues, venue, index, filteredLocations) => {
                // "Map" retains key / value order, as opposed to Object

                let country = groupedVenues.get(venue.countryCode);
                if (!country) {
                    country = {
                        displayName: venue.countryName,
                        states: new Map<string, Array<{ displayName: string; id: string }>>(),
                    };
                    groupedVenues.set(venue.countryCode, country);
                }

                let state = country.states.get(venue.state);
                if (!state) {
                    state = [];
                    country.states.set(venue.state, state);
                }

                // use the region name if: 1) region name exists 2) region name is unique 3) duplicate city name exists 4) externalId is different
                const displayName =
                    venue.region &&
                    filteredLocations.some(
                        currentLocation =>
                            currentLocation.region !== venue.region &&
                            currentLocation.city === venue.city &&
                            currentLocation.externalId !== venue.externalId,
                    )
                        ? venue.region
                        : venue.city;

                state.push({
                    id: venue.externalId,
                    displayName,
                });

                return groupedVenues;
            },
            new Map<
                string,
                {
                    displayName: string;
                    states: Map<string, Array<{ displayName: string; id: string }>>;
                }
            >(),
        );
}
